"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react"; // 添加 ReactNode 类型
import type { TFunction } from "i18next"; // For type safety with i18n

export interface Feature {
  id: string;
  key: string;
  name: string;
  description?: string | null;
}

export interface ProductRow {
  id: string;
  name: string;
  description?: string | null;
  features: Feature[];
  customFeatures?: Record<string, string | number | boolean> | Array<{
    keyName: string;
    value: string | number | boolean;
    valueType?: string;
    id?: string;
  }>;
  monthlyPrice?: number | string | null; // 允许string或number类型
  yearlyPrice?: number | string | null; // 允许string或number类型
  createdAt: string; // Dates from API are typically strings
  // Add any other fields from the API response you want to display or use
}

interface ProductColumnsProps {
  onEdit: (product: ProductRow) => void;
  onDelete: (product: ProductRow) => void;
  tFunction: TFunction;
}

export const getProductColumns = ({ onEdit, onDelete, tFunction }: ProductColumnsProps): ColumnDef<ProductRow>[] => {
  const t = tFunction; // Use the passed t function

  return [
    {
      id: "drag-handle",
      header: () => null, // No header for the drag handle column
      cell: () => null, // Cell content is rendered by SortableProductRow
      size: 60, // Adjust size as needed for the handle
    },
    {
      id: "productInfo", // Using id for a custom composite cell
      header: t("admin.activation.products.productDetails"), // New header
      cell: ({ row }) => {
        const name = row.original.name;
        const description = row.original.description;
        return (
          <div className="flex flex-col items-start gap-1">
            <div className="font-bold text-base">{name}</div>
            {description ? (
              <div className="text-sm text-muted-foreground truncate max-w-md" title={description}>
                {description}
              </div>
            ) : null}
          </div>
        );
      },
      size: 450, // Adjusted size for combined content
    },
    {
      id: "pricing",
      header: t("admin.activation.products.pricing"),
      cell: ({ row }) => {
        const monthlyPrice = row.original.monthlyPrice;
        const yearlyPrice = row.original.yearlyPrice;

        if (!monthlyPrice && !yearlyPrice) {
          return <span className="text-muted-foreground">{t("common.na")}</span>;
        }

        return (
          <div className="flex flex-col gap-1">
            {monthlyPrice !== undefined && monthlyPrice !== null && (
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2 bg-primary/10">
                  {t("admin.activation.products.monthly")}
                </Badge>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2
                  }).format(Number(monthlyPrice))}
                </span>
              </div>
            )}
            {yearlyPrice !== undefined && yearlyPrice !== null && (
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2 bg-secondary/10">
                  {t("admin.activation.products.yearly")}
                </Badge>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2
                  }).format(Number(yearlyPrice))}
                </span>
              </div>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "features",
      header: t("admin.activation.products.features"),
      cell: ({ row }) => {
        const features = row.original.features;

        if (!features || features.length === 0) {
          return <span className="text-muted-foreground">{t("common.na")}</span>;
        }

        return (
          <div className="flex flex-wrap gap-2 py-1.5">
            {features.map((feature) => (
              <Badge
                key={feature.id} // Assuming 'id' is unique and available from Feature interface
                variant="outline"
                className="font-medium" // Added for a slightly bolder look, adjust as needed
              >
                {feature.key}
              </Badge>
            ))}
          </div>
        );
      },
      size: 300, // 增加宽度以便能更好地显示特性
    },
    {
      accessorKey: "createdAt",
      header: t("settings.apiKeys.table.createdAt"),
      cell: ({ row }) => new Date(row.getValue("createdAt")).toLocaleDateString(),
      size: 150,
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("common.actions")}</div>,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">{t("admin.users.columns.openMenu")}</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onEdit(product)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("settings.apiKeys.actions.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(product)} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("settings.apiKeys.actions.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      size: 80,
    },
  ];
};